/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { GlobalHeaderBladeType, GlobalSettingsBladeTabsType } from 'src/appShell/globalHeader/types';
import { CluBuildSchemaPagePivot } from 'src/clu/appShell/cluBuildSchemaPage/CluBuildSchemaPage';
import { UIConfigsStore } from 'src/core/stores/UIConfigsStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { LandingPagePivot } from 'src/languageStudio/appShell/landingPage/components/DemoTileSection';
import { TeachingBubblesKeys } from 'src/luis/projects/events/types/TeachingBubbleDto';
import { UtteranceTableViewOptions } from 'src/luis/projects/utterances/types/UtteranceTableViewOptions';

export type UIConfigsHandlerGroup = ReturnType<typeof createUIConfigsHandlerGroup>;

/**
 * Represents a handler group for UI state settings.
 */
export const createUIConfigsHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { uiStore } = dependencies;

    /**
     * Updates the state of the global blade which includes notification,
     * feedback, settings, profile, and help blades.
     */
    const updateActiveGlobalBlade = async ({
        newBlade,
        settingsTab = 'directories'
    }: HandlerParams<{ newBlade: GlobalHeaderBladeType; settingsTab?: GlobalSettingsBladeTabsType }>) => {
        uiStore.setCurrentActiveGlobalBlade(newBlade);
        uiStore.setCurrentActiveSettingTab(settingsTab);
    };

    /**
     * Updates the visibility state of the composite entities migration
     * coach mark for the upgraded version.
     */
    const updateCompositeEntitiesMigrationCoachMarkVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setIsCompositeEntitiesMigrationCoachMarkVisible(isVisible);
        getStorage('sessionStorage').setData(LuisConstants.StorageKeys.ConfirmSuccessfulMigrationStorageKey, JSON.stringify(isVisible));
    };

    /**
     * Updates the visibility state of the composite entities migration
     * modal.
     */
    const updateEntitiesMigrationModalVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setIsEntitiesMigrationModalOpen(isVisible);
    };

    /**
     * Sets the given locale to the current portal locale.
     *
     * @param param0.locale The locale to set.
     */
    const setCurrentLocale = async ({ locale }: Pick<UIConfigsStore, 'locale'>) => {
        uiStore.setLocale(locale);
    };

    /**
     * Sets the given theme to the current portal theme.
     *
     * @param param0.theme The theme to set.
     */
    const setCurrentTheme = async ({ theme }: Pick<UIConfigsStore, 'theme'>) => {
        uiStore.setTheme(theme);
    };

    /**
     * Marks the given teaching bubble as already viewed so
     * it won't be shown again.
     *
     * @param param0.bubbleName The teaching bubble name to mark as viewed.
     */
    const markTeachingBubbleAsViewed = async ({ bubbleName }: { bubbleName: TeachingBubblesKeys }) => {
        uiStore.setTeachingBubble(bubbleName, true);
    };

    /**
     * Marks the given teaching bubble as already skipped so
     * it won't show any next bubbles.
     *
     * @param param0.bubbleName The teaching bubble name to mark as viewed.
     */
    const markTeachingBubbleAsSkipped = async ({ bubbleName }: { bubbleName: TeachingBubblesKeys }) => {
        uiStore.setSkippedTeachingBubble(bubbleName, true);
    };

    /**
     * Toggles a specific option of the utterances view options.
     *
     * @param param0.utterancesViewOption The view option name to toggle.
     * @param param0.value The view option's new state.
     */
    const toggleUtterancesViewOption = async ({ utterancesViewOption }: { utterancesViewOption: Partial<UtteranceTableViewOptions> }) => {
        const { utterancesViewOptions } = uiStore;
        uiStore.setUtterancesViewOptions({ ...utterancesViewOptions, ...utterancesViewOption });
    };

    /**
     * Sets the hidden state of edit utterance disclaimer modal.
     */
    const hideEditUtteranceDisclaimerModal = async () => {
        uiStore.setIsEditUtteranceDisclaimerModalHidden(true);
    };

    /**
     * Toggles the sidebar.
     */
    const toggleSidebar = async () => {
        uiStore.setIsSidebarCollapsed(!uiStore.isSidebarCollapsed);
    };

    /**
     * Sets the sidebar collapse state and opens a teaching bubble to notify the user.
     */
    const setIsSidebarCollapsed = async ({ isSidebarCollapsed }: HandlerParams<{ isSidebarCollapsed: boolean }>) => {
        uiStore.setIsSidebarCollapsed(isSidebarCollapsed);
    };

    /**
     * Sets the collapse state of the sidebar according to the input flag.
     *
     * @param isCollapsed boolean flag indicating if the sidebar is collapsed.
     */
    const toggleCustomTextFilesViewer = async ({ isCollapsed }: { isCollapsed: boolean }) => {
        uiStore.setIsCustomTextFilesViewerCollapsed(isCollapsed);
    };

    /**
     * Dismisses the announcement banner of the given index.
     */
    const dismissAnnouncementBanner = async ({ index }: HandlerParams<{ index: number }>) => {
        uiStore.hideAnnouncement(index);
    };

    /**
     * Updates the visibility state of the cs account select modal.
     */
    const updateCsAccountSelectModalVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setIsSwitchAccountModalHidden(!isVisible);
    };

    const updateCsAccountSelectModalProps = async ({
        isVisible,
        resourceId,
        subscriptionId
    }: HandlerParams<{ isVisible: boolean; resourceId: string; subscriptionId: string }>) => {
        uiStore.setIsSwitchAccountModalProps(!isVisible, resourceId, subscriptionId);
    };

    /**
     * Updates the visibility state of the cs account teaching bubble.
     */
    const updateCsAccountTeachingBubbleVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setIsNoAccountTeachingBubbleHidden(!isVisible);
    };

    /**
     * Updates the visibility state of the language studio sign in teaching bubble.
     */
    const updateLanguageStudioSignInTeachingBubbleVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setLanguageStudioSignInTeachingBubbleHidden(!isVisible);
    };

    /**
     * Updates the column visibility of QnaMaker edit kb page.
     */
    const toggleQnaMakerEditKbOptionalColumnsVisibility = async ({ key }: HandlerParams<{ key: string }>) => {
        uiStore.toggleQnaMakerEditKbOptionalColumnsVisibility(key);
    };

    /**
     * Updates the column visibility of QnaMaker review suggestions page.
     */
    const toggleQnaMakerReviewSuggestionsOptionalColumnsVisibility = async ({ key }: HandlerParams<{ key: string }>) => {
        uiStore.toggleQnaMakerReviewSuggestionsOptionalColumnsVisibility(key);
    };

    /**
     * Updates the visibility state of the clu migration modal.
     */
    const updateCluMigrationModalVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setIsCluMigrationModalHidden(!isVisible);
    };

    /**
     * Updates the visibility state of the clu migration migrating luis applications modal.
     */
    const updateCluMigrationLuisApplicationsnModalVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setisCluMigrationLuisApplicationsnModalHidden(!isVisible);
    };

    /**
     * Updates the visibility state of the clu migration next steps modal.
     */
    const updateCluMigrationNextStepsModalVisibility = async ({ isVisible }: HandlerParams<{ isVisible: boolean }>) => {
        uiStore.setIsCluMigrationNextStepsModalHidden(!isVisible);
    };

    /**
     * Updates the landing page active pivot
     */
    const updateLandingPageActivePivot = async ({ landingPagePivot }: HandlerParams<{ landingPagePivot: LandingPagePivot }>) => {
        uiStore.setLandingPageActivePivot(landingPagePivot);
    };

    const updateCluBuildSchemaPageActivePivot = async ({ activePivot }: HandlerParams<{ activePivot: CluBuildSchemaPagePivot }>) => {
        uiStore.setCluBuildSchemaPageActivePivot(activePivot);
    };

    return {
        toggleSidebar,
        setCurrentTheme,
        setCurrentLocale,
        setIsSidebarCollapsed,
        updateActiveGlobalBlade,
        dismissAnnouncementBanner,
        markTeachingBubbleAsViewed,
        toggleUtterancesViewOption,
        markTeachingBubbleAsSkipped,
        toggleCustomTextFilesViewer,
        updateLandingPageActivePivot,
        updateCsAccountSelectModalProps,
        hideEditUtteranceDisclaimerModal,
        updateCluMigrationModalVisibility,
        updateCluBuildSchemaPageActivePivot,
        updateCsAccountSelectModalVisibility,
        updateEntitiesMigrationModalVisibility,
        updateCsAccountTeachingBubbleVisibility,
        updateCluMigrationNextStepsModalVisibility,
        toggleQnaMakerEditKbOptionalColumnsVisibility,
        updateCluMigrationLuisApplicationsnModalVisibility,
        updateLanguageStudioSignInTeachingBubbleVisibility,
        updateCompositeEntitiesMigrationCoachMarkVisibility,
        toggleQnaMakerReviewSuggestionsOptionalColumnsVisibility
    };
};
